<template>
  <div>

    <!-- import export buttons (ocultos en modo embebido por chapter) -->
    <div v-if="depth==0 && type=='resource' && !chapterId" class="flex items-center justify-end space-x-2 mb-4">
      <v-button @click="showImportExecutedQuantity = true" size="small">
        <b-icon icon="file-import" pack="fas" />
        {{ $t('import_executed_quantity') }}
      </v-button>
      <v-button @click="exportModal = true" color="green" size="small">
        <template v-if="!isExporting">
          <b-icon icon="file-export" pack="fas" />
          {{ $t('export') }}
        </template>
        <template v-else>
          <i class="fa fa-spinner fa-spin"></i> {{ $t('loading') }}...
        </template>
      </v-button>
    </div>


    <!-- Filtros: ocultos cuando se monta con un chapterId (uso embebido desde el tab Etiquetas) -->
    <measurement-filters v-if="depth==0 && !chapterId"
      ref="filtersRef"
      :type="type"
      @filters-updated="filtersUpdated"
      @epc-filter-orders-loaded="loadedOrders" />

    <div class="flex w-full">
      <div :class="showImportExecutedQuantity ? 'w-3/4 pr-2' : 'w-full'">
        <div class="text-sm" :class="depth==0 ? 'panel' : ''">

          <template>
            <!-- Contador de resultados -->
            <div v-if="total">
              <h4 class="text-right">
                <b>{{$t('count_results')}}: {{ total }}</b>
              </h4>
            </div>

            <b-table
              :data="list"
              :loading="isLoadingTable"
              icon-pack="fas"
              :paginated="depth==0"
              backend-pagination
              :per-page="perPage"
              :total="total"
              @page-change="onPageChange"
              :current-page.sync="currentPage"

              backend-sorting
              :default-sort="orderField"
              :default-sort-direction="orderDir"
              @sort="onSort"
              
              detailed
              detail-key="id"

              class="text-xs border-blue-lighter"
              bordered
              narrowed
              striped

              :sticky-header="stickyHeader"
              :mobile-cards="false"
              @details-open="onDetailToggled"
              :scrollable="true"
              >

              <template slot="empty">
                <div v-if="! isLoadingTable" class="m-4 text-lg text-dark-grey">
                  <span>{{ $t('empty_assignments_table_msg') }}</span>
                </div>
              </template>

              <template>

                <b-table-column field="type" :label="$t('type')" cell-class="vertical-center" sortable v-slot="items">
                  {{ $t(items.row.type) }}
                </b-table-column>

                <b-table-column field="name" :label="$t('name')" cell-class="vertical-center" sortable v-slot="items">
                  <span :class="canOpenEditModal(items.row) ? 'clickable' : ''" @click="onClickEdit(items.row)">{{
                    items.row.name }}</span>
                </b-table-column>

                <b-table-column field="unit_name" :label="$t('measurement_unit')" cell-class="vertical-center" sortable
                  v-slot="items">
                  <span v-if="items.row.unit_name">{{ items.row.unit_name }}</span>
                  <span v-else-if="items.row.type=='task'">%</span>
                  <span v-else>---</span>
                </b-table-column>

                <b-table-column field="cost" :label="$t('price')" cell-class="vertical-center" centered sortable
                  v-slot="items">
                  <span v-if="items.row.unit_price != 0">
                    {{ twoDecimals(items.row.cost) }} {{project.currency}}
                  </span>
                  <span v-else>---</span>
                </b-table-column>

                <b-table-column field="total_units_initial" :label="$t('total_units_initial')"
                  cell-class="vertical-center" centered sortable v-slot="items">
                  <span v-if="!isTask(items.row) && items.row.total_units_initial !== null">
                    {{ items.row.total_units_initial }}
                  </span>
                  <span v-else>---</span>
                </b-table-column>

                <b-table-column field="total_quantity" :label="$t('total_quantity')" cell-class="vertical-center"
                  centered sortable v-slot="items">
                  <span v-if="!isTask(items.row)">{{ items.row.total_quantity }}</span>
                  <span v-else-if="type=='task'">
                    {{ twoDecimals(items.row.weight) }} %
                  </span>
                  <span v-else>---</span>
                </b-table-column>

                <b-table-column field="executed_quantity" :label="executedQuantityLabel" cell-class="vertical-center"
                  sortable centered v-slot="items">
                  <span>{{ items.row.executed_quantity }} {{ items.row.executed_quantity && type == 'task' ? '%' : ''
                    }}</span>
                </b-table-column>

                <b-table-column field="remaining" :label="$t('remaining_to_inspect')" cell-class="vertical-center"
                  centered sortable v-slot="items">
                  {{ getRemaining(items.row) }} {{ type == 'task' ? '%' : '' }}
                </b-table-column>


                <b-table-column field="pending_validation" :label="pendingQuantityLabel" cell-class="vertical-center"
                  centered sortable v-slot="items">
                  <span
                    v-if="items.row.type != 'certifier' && items.row.pending_validation_quantity && items.row.pending_validation_quantity != 0">
                    {{ twoDecimals(items.row.pending_validation_quantity) }}
                    <span @click="onClickValidate(items.row)" v-if="project.can_certificate">
                      <b-tooltip :label="$t('validate')" position="is-top">
                        <b-icon pack="fas" icon="thumbs-up"></b-icon>
                      </b-tooltip>
                    </span>
                  </span>
                </b-table-column>

                <!-- Doble check de calidad: cantidad validada por inspección pendiente de calidad -->
                <b-table-column v-if="project.has_quality_validation && type == 'resource'" field="pending_quality_validation"
                  :label="$t('pending_quality_validation_quantity')" cell-class="vertical-center" centered v-slot="items">
                  <span
                    v-if="items.row.pending_quality_validation_quantity && items.row.pending_quality_validation_quantity != 0">
                    {{ twoDecimals(items.row.pending_quality_validation_quantity) }}
                    <span @click="onClickValidateQuality(items.row)" v-if="project.can_validate_quality">
                      <b-tooltip :label="$t('validate')" position="is-top">
                        <b-icon pack="fas" icon="clipboard-check"></b-icon>
                      </b-tooltip>
                    </span>
                  </span>
                </b-table-column>

                <b-table-column field="completed" :label="$t('activity_status_done_inspected')"
                  cell-class="vertical-center" centered sortable v-slot="items">
                  <b-tooltip :label="items.row.completed ? $t('completed'):$t('uncompleted')" position="is-top"
                    v-html="isCompleted(items.row)" />
                </b-table-column>

                <b-table-column v-if="project.has_quality_validation && type == 'resource'" field="quality_completed"
                  :label="$t('activity_status_done_quality')" cell-class="vertical-center" centered v-slot="items">
                  <b-tooltip :label="isQualityCompleted(items.row) ? $t('completed'):$t('uncompleted')" position="is-top"
                    v-html="qualityCompletedIcon(items.row)" />
                </b-table-column>
                <b-table-column v-if="project.can_progress" field="actual_progress" :label="$t('actual_quantity')"
                  cell-class="vertical-center" sortable centered v-slot="items">
                  <span>{{ items.row.actual_progress }} {{ items.row.actual_progress && type == 'task' ? '%' : '' }}
                  </span>

                </b-table-column>
                <b-table-column v-if="project.can_progress" field="actual_remaining" :label="$t('actual_remaining')"
                  cell-class="vertical-center" centered sortable v-slot="items">
                  {{ getActualRemaining(items.row) }} {{ type == 'task' ? '%' : '' }}
                </b-table-column>
                <b-table-column v-if="project.can_progress" field="completed" :label="$t('activity_status_done_actual')"
                  cell-class="vertical-center" centered sortable v-slot="items">

                  <b-tooltip :label="items.row.completed ? $t('completed'):$t('uncompleted')" position="is-top"
                    v-html="isCompletedActual(items.row)" />
                </b-table-column>


                <b-table-column field="partner" :label="$t('partner')" cell-class="vertical-center" sortable
                  v-slot="items">
                  <span>{{ getNamePartner(items.row.work_order_id) }}</span>
                </b-table-column>

                <b-table-column field="purchase_order" :label="$t('purchase_order')" cell-class="vertical-center"
                  sortable v-slot="items">
                  <span>{{ items.row.order_code }}</span>
                </b-table-column>

                <b-table-column field="position" :label="$t('position')" cell-class="vertical-center" sortable
                  v-slot="items">
                  <span>{{ items.row.position }}</span>
                </b-table-column>

                <b-table-column v-if="type=='task'" field="status" :label="$t('status')" cell-class="vertical-center"
                  centered sortable v-slot="items">
                  <b-tooltip class="text-center" v-if="getIssueTaskIcon(items.row) != null"
                    :label="$t(getIssueTaskIcon(items.row).name)" position="is-left">
                    <b-icon :icon="getIssueTaskIcon(items.row).icon" pack="fas" size="is-small"
                      :class="getIssueTaskIcon(items.row).color"></b-icon>
                  </b-tooltip>
                </b-table-column>

              </template>

              <template slot="detail" slot-scope="items">

                <div v-if="isRowLoading(items.row.id)" class="p-4 text-center">
                  <p class="text-sm mt-2">{{ $t('loading') }}... </p>
                </div>

                <!-- si tiene children mostramos los children antes que assignable -->
                <measurement v-else-if="hasDetailChildren(items.row)"
                  :assignments="items.row.children" :type="type" :depth="depth + 1" @reload="reload" />

                <b-table v-else-if="getDetailAssignableList(items.row).length > 0"
                  :data="getDetailAssignableList(items.row)"
                  icon-pack="fas"
                  class="text-xs border-blue-lighter"
                  :class="isSubActivity(getDetailAssignableList(items.row)[0]) ? 'subactivities' : ''" bordered narrowed striped
                  >
                  <template slot="empty">
                    <div class="m-4 text-lg text-dark-grey">
                      <span>{{ $t('no_results') }}</span>
                    </div>
                  </template>

                  <template>
                    <b-table-column field="planning_code" :label="$t('code')" cell-class="w-32 vertical-center"
                      v-slot="activities">
                      <div>
                        {{ activities.row.planning_code }}
                      </div>
                    </b-table-column>

                    <b-table-column field="name" :label="$t('name')" cell-class="vertical-center" v-slot="activities">

                      <div class="cursor-pointer" @click="openActivity(activities.row)">
                        <activity-time-status :date="activities.row.current_end_date" class="mr-2"
                          v-if="activities.row.status != 4 && activities.row.status != 6" />
                        <b-tooltip v-if="activities.row.critical_path != null" position="is-right"
                          :label="$t('critical_path')">
                          <b-icon class="fa is-small fa-project-diagram" style="color:#e3342f" />
                        </b-tooltip>
                        {{ activities.row.name }}
                      </div>
                    </b-table-column>

                    <b-table-column field="progress" :label="progressLabel" cell-class="w-32 vertical-center" centered
                      v-slot="activities">
                      <progress-bar :step="parseFloat(activities.row.progress || 0)" color="blue-light"
                        class="w-full" />
                    </b-table-column>

                    <b-table-column field="progress_actual" :label="$t('actual_progress')"
                      cell-class="w-32 vertical-center" centered v-slot="activities">
                      <progress-bar :step="parseFloat(activities.row.progress_actual || 0)" color="blue-light"
                        class="w-full" />
                    </b-table-column>

                    <b-table-column field="current_start_date" :label="$t('start')" cell-class="w-25 vertical-center"
                      centered v-slot="activities">
                      {{ activities.row.current_start_date }}
                    </b-table-column>

                    <b-table-column field="current_end_date" :label="$t('end')" cell-class="w-25 vertical-center"
                      centered v-slot="activities">
                      {{ activities.row.current_end_date }}
                    </b-table-column>

                    <b-table-column field="status" :label="$t('status')" cell-class="w-32 vertical-center" centered
                      v-slot="activities">
                      <div class="flex justify-center items-center py-1">
                        <activity-status-label :status="activities.row.status" fontSize="0.65" />
                      </div>
                    </b-table-column>


                    <b-table-column field="pending_validation" :label="pendingQuantityLabel" cell-class="vertical-center" centered
                        sortable v-slot="items">
                      <span v-if="items.row.type != 'certifier' && items.row.pending_validation_quantity && items.row.pending_validation_quantity != 0">
                        {{ twoDecimals(items.row.pending_validation_quantity) }}
                        <span @click="onClickValidate(items.row)">
                          <b-tooltip
                            :label="$t('validate')"
                            position="is-top"
                          >
                            <b-icon pack="fas" icon="thumbs-up"></b-icon>
                          </b-tooltip>
                        </span>
                      </span>
                    </b-table-column>

                    <b-table-column field="completed" :label="$t('activity_status_done_inspected')" cell-class="vertical-center" centered
                                    sortable v-slot="items">
                      <b-tooltip
                        :label="items.row.completed ? $t('completed'):$t('uncompleted')"
                        position="is-top"
                        v-html="isCompleted(items.row)"
                      />
                    </b-table-column>
                    <b-table-column v-if="project.has_quality_validation && type == 'resource'" field="quality_completed"
                      :label="$t('activity_status_done_quality')" cell-class="vertical-center" centered v-slot="items">
                      <b-tooltip
                        :label="isQualityCompleted(items.row) ? $t('completed'):$t('uncompleted')"
                        position="is-top"
                        v-html="qualityCompletedIcon(items.row)"
                      />
                    </b-table-column>
                    <b-table-column v-if="project.can_progress" field="actual_progress" :label="$t('actual_quantity')" cell-class="vertical-center" sortable
                        centered v-slot="items">
                      <span>{{ items.row.actual_progress }} {{  items.row.actual_progress && type == 'task' ? '%' : '' }} </span>

                    </b-table-column>
                    <b-table-column v-if="project.can_progress" field="actual_remaining" :label="$t('actual_remaining')" cell-class="vertical-center" centered sortable v-slot="items">
                      {{ getActualRemaining(items.row)  }} {{ type == 'task' ? '%' : '' }}
                    </b-table-column>
                    <b-table-column v-if="project.can_progress" field="completed" :label="$t('activity_status_done_actual')" cell-class="vertical-center" centered
                                    sortable v-slot="items">

                      <b-tooltip
                        :label="items.row.completed ? $t('completed'):$t('uncompleted')"
                        position="is-top"
                        v-html="isCompletedActual(items.row)"
                      />
                    </b-table-column>


                    <b-table-column field="partner" :label="$t('partner')" cell-class="vertical-center" sortable v-slot="items">
                      <span>{{ getNamePartner(items.row.work_order_id) }}</span>
                    </b-table-column>

                    <b-table-column field="purchase_order" :label="$t('purchase_order')" cell-class="vertical-center" sortable v-slot="items">
                      <span>{{ items.row.order_code }}</span>
                    </b-table-column>

                    <b-table-column field="position" :label="$t('position')" cell-class="vertical-center" sortable v-slot="items">
                      <span>{{ items.row.position }}</span>
                    </b-table-column>

                    <b-table-column v-if="type=='task'" field="status" :label="$t('status')" cell-class="vertical-center" centered sortable v-slot="items">
                      <b-tooltip class="text-center" v-if="getIssueTaskIcon(items.row) != null"
                        :label="$t(getIssueTaskIcon(items.row).name)" position="is-left">
                        <b-icon :icon="getIssueTaskIcon(items.row).icon" pack="fas" size="is-small" :class="getIssueTaskIcon(items.row).color"></b-icon>
                      </b-tooltip>
                    </b-table-column>

                  </template>

                  <template slot="detail" slot-scope="items">

                    <div v-if="isRowLoading(items.row.id)" class="p-4 text-center">
                      <p class="text-sm mt-2">{{ $t('loading') }}... </p>
                    </div>
                    <!-- si tiene children mostramos los children antes que assignable -->
                    <measurement
                      v-else-if="hasDetailChildren(items.row)"
                      :assignments="items.row.children"
                      :type="type"
                      :depth="depth + 1"
                      @reload="reload"
                    />

                    <b-table v-else-if="getDetailAssignableList(items.row).length > 0"
                      :data="getDetailAssignableList(items.row)"
                      icon-pack="fas"

                      class="text-xs border-blue-lighter"
                      :class="isSubActivity(getDetailAssignableList(items.row)[0]) ? 'subactivities' : ''"
                      bordered
                      narrowed
                      striped
                    >
                      <template slot="empty">
                        <div class="m-4 text-lg text-dark-grey">
                          <span>{{ $t('no_results') }}</span>
                        </div>
                      </template>

                      <template>
                        <b-table-column field="planning_code" :label="$t('code')" cell-class="w-32 vertical-center" 
                          v-slot="activities">
                          <div>
                            {{ activities.row.planning_code }}
                          </div>
                        </b-table-column>

                        <b-table-column field="name" :label="$t('name')" cell-class="vertical-center" v-slot="activities">

                          <div class="cursor-pointer"
                              @click="openActivity(activities.row)">
                            <activity-time-status :date="activities.row.current_end_date" class="mr-2"
                                                  v-if="activities.row.status != 4 && activities.row.status != 6"/>
                            <b-tooltip v-if="activities.row.critical_path != null" position="is-right"
                                      :label="$t('critical_path')">
                              <b-icon class="fa is-small fa-project-diagram" style="color:#e3342f"/>
                            </b-tooltip>
                            {{ activities.row.name }}
                          </div>
                        </b-table-column>

                        <b-table-column field="progress" :label="progressLabel" cell-class="w-32 vertical-center" centered
                          v-slot="activities">
                          <progress-bar
                            :step="parseFloat(activities.row.progress || 0)"
                            color="blue-light"
                            class="w-full"
                          />
                        </b-table-column>

                        <b-table-column field="progress_actual" :label="$t('actual_progress')" cell-class="w-32 vertical-center" centered
                          v-slot="activities">
                          <progress-bar
                            :step="parseFloat(activities.row.progress_actual || 0)"
                            color="blue-light"
                            class="w-full"
                          />
                        </b-table-column>

                        <b-table-column field="current_start_date" :label="$t('start')" cell-class="w-25 vertical-center" centered
                          v-slot="activities">
                          {{ activities.row.current_start_date }}
                        </b-table-column>

                        <b-table-column field="current_end_date" :label="$t('end')" cell-class="w-25 vertical-center" centered
                          v-slot="activities">
                          {{ activities.row.current_end_date }}
                        </b-table-column>

                        <b-table-column field="status" :label="$t('status')" cell-class="w-32 vertical-center" centered
                          v-slot="activities">
                          <div class="flex justify-center items-center py-1">
                            <activity-status-label :status="activities.row.status" fontSize="0.65" />
                          </div>
                        </b-table-column>
                      </template>
                    </b-table>
                  </template>

                </b-table>
              </template>

            </b-table>
          </template>
        </div>
      </div>
      <div v-if="showImportExecutedQuantity" class="w-1/4 min-w-0">
        <div class="panel p-2 my-4 import-panel">
          <div class="flex items-center justify-between w-full mb-5">
            <div>
              <label><b>{{ $t('import_executed_quantity') }}</b></label>
            </div>
            <div>
              <b-tooltip :label="$t('close')" position="is-left">
                <div
                  class="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer bg-red text-grey-lightest"
                  @click="close()">
                  <b-icon pack="fas" icon="times" size="is-small" />
                </div>
              </b-tooltip>
            </div>
          </div>
          <div class="w-full pb-3">
            <import-entities ref="importEntities" :can-overwrite="false" :project-id="project ? project.id : 0" :with-history="true"
              entity-type="assignments-executedquantity" @success="onImportSuccess" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para Editar -->
    <assignment-update-modal ref="assignment_update_modal" entity-type="activity" :entity-id="entityId"
      @reload="reload" />

    <!-- Actualiza la tabla automáticamente, de momento se queda -->
    <!-- <mugen-scroll :handler="fetchData" :should-handle="!isLoadingTable" scroll-container="wrap"></mugen-scroll>
    <b-loading :is-full-page="false" :active.sync="isLoadingTable" :can-cancel="false"></b-loading> -->
    <assignment-validate-quantity-modal ref="assignment_validate_quantity_modal" entity-type="activity"
      :entity-id="entityId" />

    <b-modal v-model="exportModal" title="Selecciona el tipo de exportación" :width="300">
      <div class="flex flex-col bg-white p-4 rounded-lg">
        <div class="flex items-center">
          <span class="text-blue md:text-lg font-bold">{{ $t('select_template') }}</span>
        </div>
        <div class="border-b border-gray-300 mt-4"></div>
        <div class="flex flex-col mt-4">
          <div v-for="(option, idx) in exportOptions" :key="idx" class="flex items-center cursor-pointer mt-2"
            @click="exportMeasurements(type, option.value); exportModal = false">
            <b-icon pack="fas" icon="file-export" class="mr-2 text-blue"></b-icon>
            <span class="text-blue">{{ option.label }}</span>
          </div>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
  import Vue from "vue"
  import axios from 'axios'
  import {mapGetters} from 'vuex'
  import MugenScroll from 'vue-mugen-scroll'
  import Buefy from 'buefy'
  import BIcon from "buefy/src/components/icon/Icon";
  import EventBus from '~/plugins/bus'
  import assignmentUpdateModal from "·/components/assignment-update-modal";
  import activityTimeStatus from '·/components/activity-time-status'
  import activityStatusLabel from '·/components/activity-status-label'
  import MeasurementFilters from '·/components/measurement-filters';
  import projectAssignmentsReport from '·/components/project-assignments-report';
  import AssignmentValidateQuantityModal from '·/components/assignment-validate-quantity-modal';
  import ImportEntities from '·/components/import-entities';
  Vue.use(Buefy);

  export default {
    name: "measurement",
    middleware: 'auth',
    title: 'measurements_page_title',
    subtitle: 'measurements_page_subtitle',
    menuOption: '2-6',

    components: {
      BIcon,
      MugenScroll,
      assignmentUpdateModal,
      activityTimeStatus,
      activityStatusLabel,
      MeasurementFilters,
      projectAssignmentsReport,
      AssignmentValidateQuantityModal,
      ImportEntities
    },

    props: {
      type: { type: String, default: 'resource' },
      depth: { type: Number, default: 0 },
      assignments: { type: Array, default: () => [] },
      // Si esta seteado, todas las llamadas filtran por este capitulo (incluye descendientes).
      // Lo usa el tab "Etiquetas" para listar solo las mediciones del capitulo elegido.
      chapterId: { type: Number, default: null },
    },

    computed: {
      ...mapGetters({
        user: 'auth/user',
        project: 'app/project',
        mobile: 'device/mobile',
        company: 'app/company',
      }),

      stickyHeader() { return this.mobile.on_mobile}, // para fiar el header de la tabla cuando se esta mostrando en movil

      executedQuantityLabel() {
        let label = this.$t('executed_quantity');
        if (this.company.alias == 'welink') {
          label = this.$t('inspected_quantity');
        }
        return label;
      },

      pendingQuantityLabel() {
        let label = this.$t('pending_validation_quantity');
        if (this.company.alias == 'welink') {
          label = this.$t('pending_inspect');
        }
        return label;
      },

      progressLabel() {
        let label = this.$t('progress_label');
        if (this.company.alias == 'welink') {
          label = this.$t('progress_inspected_label');
        }
        return label;
      }
    },

    data: () => ({
      // paginationRows: 10,
      entityId: 0, // ID de la actividad clicada
      isLoadingTable: true,
      page: 1,
      currentPage: 1,
      last: 1,
      perPage: 25,
      total: 0,
      orderField: "name",
      orderDir: "asc",
      list: [],
      // assignable se 
      filterPendingValidation: null,

      orders: [], // es cargado desde el componente measurement-filters
      loadingRows: {},
      activityList: [],
      isExporting: false,
      showImportExecutedQuantity: false,
      lineStatus: {
        ISSUE: 20,
        ISSUE_SOLVED: 21,
        ISSUE_WITHOUT_LOCK: 22,
        ISSUE_LOCK_TASK: 23,
        ISSUE_LOCK_ACTIVITY: 24,
        ISSUE_LOCK_PROJECT: 25,
        ISSUE_LOCK_SOLVED: 26,
      },
      exportModal: false,
      exportOptions: [],
    }),

    created() {
      this.exportOptions = [
        { value: '0', label: this.$t('simple') },
        { value: '1', label: this.$t('resources_breakdown') }
      ];
    },

    mounted() {
      this.applyFilters(this.page);
      let self = this;

      EventBus.$on("refreshAssignmentChangedNotifications", function (e) {
        if (self.project.id === e.project_id) {
          self.isLoading = true;
          self.applyFilters(self.page);
        }
      });
      EventBus.$on("refreshAssignmentCreatedNotifications", function (e) {
        if (self.project.id === e.project_id) {
          self.isLoading = true;
          self.applyFilters(self.page);
        }
      });
      EventBus.$on("refreshAssignmentDeletedNotifications", function (e) {
        if (self.project.id === e.project_id) {
          self.isLoading = true;
          self.applyFilters(self.page);
        }
      });
    },

    beforeDestroy(){
      EventBus.$off("refreshAssignmentChangedNotifications");
      EventBus.$off("refreshAssignmentCreatedNotifications");
      EventBus.$off("refreshAssignmentDeletedNotifications");
    },

    methods: {
      async getList(args = []) {
        // profundidad es mayor que 0 si es un subcomponente cargamos directamente los assignments pasados
        if (this.depth > 0) {
          this.isLoadingTable = false;
          this.list = this.assignments;
          return;
        }

        // where_type ya viene en los filtros desde measurement-filters.vue
        // args.with_lines = 1;

        // const {data} = await axios.get('/api/v2/project/' + this.project.id + '/assignments?page=' + this.page, {params: args});
        const {data} = await axios.get('/api/v2/project/' + this.project.id + '/assignments', {params: args});

        // Last and total
        this.last = data.lastPage;
        this.total = data.total;

        if (data && data.assignments) {
          this.list = data.assignments;
        } else {
          this.$notify.error('error_loading_assignment_data');
        }

        this.isLoadingTable = false;
      },

      async getChildren(parentId) {
        let args = {};
        args.where_type = this.type;
        args.parent_assignment_id = parentId;
        args.order_dir = this.orderDir;
        args.order_field = this.orderField;
        args.per_page = 200;
        
       return axios.get('/api/v2/project/' + this.project.id + '/assignments', {params: args});
      },

      customizeFilters() {
        let filters = this.depth === 0 ? (this.$refs.filtersRef ? this.$refs.filtersRef.generateFilters() : {}) : {};
        // filters.with_parent = 1;
        // filters.with_assignments = 1;
        filters.order_field = this.orderField;
        filters.order_dir = this.orderDir;
        filters.per_page = this.perPage;
        if( typeof filters.where_pending_validation === 'undefined' &&  this.$route.query.pending_validation ) {
          filters.where_pending_validation = 'pending'
        }
        // Si este componente fue montado con un chapterId (tab Etiquetas), heredamos el filtro por capitulo
        // y aseguramos where_type (que normalmente lo agrega measurement-filters cuando esta visible).
        if (this.chapterId) {
          filters.where_chapter_id = this.chapterId;
          if (!filters.where_type) {
            filters.where_type = this.type;
          }
        }
        return filters;
      },

      async onDetailToggled(row) {
        if (this.isRowLoading(row.id)) {
          return
        }

        this.$set(this.loadingRows, row.id, true)

        try {
          if (row.children_count > 0 && !row.children) {
            const result = await this.getChildren(row.id)
            this.$set(row, 'children', result.data.assignments || [])
          } else if (!row.children_count && row.assignable_id) {
            const assignableList = this.getDetailAssignableList(row)
            if (!assignableList.length) {
              const result = await this.getAssignable(row.id, row.assignable_id)
              const activity = result.data || result
              this.$set(row, 'assignable', activity ? [activity] : [])
            }
          }
        } catch (error) {
          console.error('Error cargando detalle de medición:', error)
          if (row.children_count > 0) {
            this.$set(row, 'children', [])
          } else {
            this.$set(row, 'assignable', [])
          }
        } finally {
          this.$delete(this.loadingRows, row.id)
        }
      },

      hasDetailChildren(row) {
        return row.children && row.children.length > 0
      },

      getDetailAssignableList(row) {
        if (!row.assignable) {
          return []
        }

        return Array.isArray(row.assignable) ? row.assignable : [row.assignable]
      },

      isRowLoading(rowId) {
        return !!this.loadingRows[rowId]
      },

      async getAssignable(id, assignableId) {
        // Esta función debe devolver los datos que se asignan a row.assignable
        const response = await axios.get(`/api/v2/activity/${assignableId}`, {});
        return response.data
      },

      async getActivities() {

      },

      /**
       * Cambia la ventana a la tabla de actividades normal
       */
      displayTable() {
        this.$router.push({name: 'project.activities'});
      },

      displayKanban() {
        this.$router.push({name: 'project.activities.kanban'});
      },

      displayGantt() {
        this.$router.push({name: 'project.activities.gantt'});
      },

      filtersUpdated() {
        this.isLoadingTable = true;
        this.applyFilters(1);
      },

      applyFilters(page) {
        let args = this.customizeFilters();
        args.page = (page <= this.last) ? page : 1;
        this.currentPage = args.page;
        this.getList(args);
      },

      fetchData() {
        if (this.page <= this.last) {
          this.isLoadingTable = true;
          this.applyFilters(this.page);
        }
      },

      onPageChange(page) {
        this.page = page;
        this.fetchData()
      },

      onSort(field, order) {
        this.orderField = field;
        this.orderDir = order;
        this.fetchData()
      },

      sortByCompleted(a, b, isAsc) {
        return isAsc ?
          a.completed - b.completed :
          b.completed - a.completed
      },

      isCompleted(row) {
        if (row.completed)
          return '<i class="fas fa-check text-green"/>';

        return '<i  class="fas fa-times text-red"/>';

      },

      isCompletedActual(row) {
        // Si actual_progress no está definido o es null, no está completo
        if (row.actual_progress == null) {
          return '<i class="fas fa-times text-red"/>';
        }

        let isCompleted = false;

        if (row.type === 'task') {
          isCompleted = parseFloat(row.actual_progress) >= 100;
        } else if (row.type === 'resource') {
          isCompleted = parseFloat(row.actual_progress) >= parseFloat(row.total_quantity);
        }

        return isCompleted
          ? '<i class="fas fa-check text-green"/>'
          : '<i class="fas fa-times text-red"/>';
      },

      // Modal para confirmar cantidades ejecutadas
      onClickValidate(row) {
        // User es usuario certificador del proyecto
        // if (!this.apiProject.can_validate)
        // if (!this.project.can_validate)
        //   return;

        // this.entityId = row.parentId;
        this.$refs.assignment_validate_quantity_modal.show(row.id);
      },

      // Modal para el doble check de calidad de cantidades ya validadas por inspección
      onClickValidateQuality(row) {
        this.$refs.assignment_validate_quantity_modal.show(row.id, null, true);
      },

      // Doble check de calidad completado: inspección completada y sin cantidades pendientes de calidad
      isQualityCompleted(row) {
        return !!row.completed && !(parseFloat(row.pending_quality_validation_quantity) > 0);
      },

      qualityCompletedIcon(row) {
        return this.isQualityCompleted(row)
          ? '<i class="fas fa-check text-green"/>'
          : '<i  class="fas fa-times text-red"/>';
      },

      getIssueTaskIcon(assignment) {
       
        const lineStatus = this.lineStatus

        // mapa de reglas para status
        const statusMap = {
          [lineStatus.ISSUE]: { icon: 'exclamation-triangle', color: 'text-yellow', name: 'task_issue'},
          [lineStatus.ISSUE_WITHOUT_LOCK]: { icon: 'exclamation-triangle', color: 'text-orange', name: 'task_issue_without_blocking'},
          [lineStatus.ISSUE_LOCK_TASK]: { icon: 'exclamation-triangle', color: 'text-red', name: 'task_issue_with_task_blocking'},
          [lineStatus.ISSUE_LOCK_ACTIVITY]: { icon: 'hand', color: 'text-orange', name: 'task_issue_with_activity_blocking'},
          [lineStatus.ISSUE_LOCK_PROJECT]: { icon: 'hand', color: 'text-red', name: 'task_issue_with_project_blocking'},
        }

        // revisar solo las líneas del assignment actual
        if (assignment.lines) {
          
          for (const line of assignment.lines) {
            if (statusMap[line.status]) {
              return statusMap[line.status]
            }
          }
        }

        return null
      },

      getRowClass() {
        return '';
      },

      getAssignmentStatusColor(row) {
        switch (row.alert) {
          case -1:
            return 'bg-blue';

          case 0:
            return 'bg-green';

          case 1:
            return 'bg-red';

          default:
            return 'bg-blue';
        }
      },

      getAssignmentStatusLabel(row) {
        switch (row.alert) {
          case -1:
            return 'assignments-under';

          case 0:
            return 'assignments-completed';

          case 1:
            return 'assignments-over';
        }
      },

      /**
       * Calcula el estado de la medición
       * @param row
       * @returns {number}
       */
      getAssignmentStatus(row) {
        if (row.type === 'task')
          if (row.completed)
            return 0; // perfecto
          else return -1;// por debajo, tarea aun no completada

        if (row.executed_quantity < row.total)
          return -1; // por debajo
        else if (row.executed_quantity > row.total)
          return 1; // por encima
        else return 0; // cantidad exacta
      },

      /**
       * Comprueba si puede abrir el modal de editado para una fila en concreto
       */
      canOpenEditModal(row) {
        if (row.user_id != this.user.id && this.isTask(row)) return false;
        return true;
      },

      /**
       * Abre el modal para editar una medición
       */
      onClickEdit(row) {
        if (!this.canOpenEditModal(row)) {
          return;
        }
        this.entityId = row.assignable_id;
        this.$refs.assignment_update_modal.show(row);
      },

      openActivity(activity) {
        if (!activity.parent_id) {
          this.$router.push({name: 'activity.home', params: { activity_id: activity.id } } )
        } else {
          this.$router.push({
            name: 'subactivity.home',
            params: { activity_id: activity.parent_id, subactivity_id: activity.id }
          })
        }
      },

      getRemaining(row) {
        if (this.isTask(row)) return 100 - row.executed_quantity;

        if (row.completed)
          return this.twoDecimals(0);

        return this.twoDecimals(row.total_quantity - row.executed_quantity);
      },

      getActualRemaining(row) {
        if (this.isTask(row)) return 100 - row.actual_progress;

        if (row.completed)
          return this.twoDecimals(0);

        return this.twoDecimals(row.total_quantity - row.actual_progress);
      },

      isSubActivity(row) {
        return row.parent_id != null;
      },

      isTask(row) {
        if (row.type === 'task')
          return true;
      },

      reload(data) {
        self.isLoading = true;
        this.applyFilters(this.page);
      },

      /**
       * Convierte el numero a dos decimales obligatoriamente
       * @param number
       * @returns {string}
       */
      twoDecimals(number) {
        if (number == null)
          number = 0;
        return parseFloat(number).toFixed(2);
      },

      loadedOrders(data) {
        this.orders = data
      },

      getNamePartner(idOrder) {

        if (! idOrder) return null
        let name = null
        let order = this.orders.find(o => idOrder == o.id)
        if (order) {
          let partners = order.partners.map(p => p.name )
          name = partners.join(',')
        }

        return name
      },

      async exportMeasurements(type, option) {
        this.isExporting = true;
        // Asegura la primera letra en mayúscula
        const typeFormatted = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        const filters = this.customizeFilters();
        try {
          const response = await axios.post(
            '/api/v2/export/assignments-workzone',
            {
              projectId: this.project.id,
              type: typeFormatted,
              exportType: option,
              filters: filters,
            },
            { responseType: 'blob' }
          );
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `quantities_${typeFormatted}_${this.project.id}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (error) {
          this.$notify.error(this.$t('error_export'));
        } finally {
          this.isExporting = false;
        }
      },

      onImportSuccess() {
        // Solo cerrar si NO hay warnings
        if (
          this.$refs.importEntities &&
          Array.isArray(this.$refs.importEntities.warnings) &&
          this.$refs.importEntities.warnings.length === 0
        ) {
          this.showImportExecutedQuantity = false;
        }
        // Si hay warnings, deja el panel abierto para que el usuario los vea
        this.applyFilters(this.page);
      },

      close() {
        this.showImportExecutedQuantity = false;
      }

    },

  };
</script>

<style lang="scss" scoped>
  .panel::v-deep .b-table .table-wrapper.has-sticky-header tr:first-child th {
    background: hsl(220, 43%, 53%);
  }
  .vertical-center {
    vertical-align: inherit !important;
  }

  .clickable {
    cursor: pointer;
  }

  .subactivities {
    .table th {
      background-color: #89a4d6;
    }

    &.b-table .table.is-bordered th.is-current-sort, &.b-table .table.is-bordered th.is-sortable:hover {
      background-color: #89a4d6 !important;
    }
  }

  .b-table .table-wrapper.has-sticky-header tr:first-child th {
    // background-color: $primary !important;
    min-height: 3rem;
  }

  // Estilos para el panel de importación lateral
  .import-panel {
    overflow-x: auto;
  }
</style>
